import { useEffect, useRef } from "react";
import type { RoomView } from "@grundbesitz/shared";
import { botLabel, colorHex, formatMoney } from "../../lib/format";
import { isSpectating, playerRows } from "../../lib/gameView";
import type { PlayerRow } from "../../lib/gameView";
import { useStore } from "../../lib/store";
import { TokenIcon } from "../board/TokenIcon";
import { useUiStore } from "./uiStore";

type ChipTone = "accent" | "warn" | "danger" | "muted";

const chipClass: Record<ChipTone, string> = {
  accent: "bg-accent text-accent-ink",
  warn: "border border-warn text-warn",
  danger: "border border-danger text-danger",
  muted: "bg-surface-2 text-ink-2",
};

function Chip({ tone, children }: { tone: ChipTone; children: string }) {
  return <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${chipClass[tone]}`}>{children}</span>;
}

/** An emoji that drifts up and fades out. Uses the Web Animations API so reduced motion can be honored. */
function FloatingEmoji({ emoji }: { emoji: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const node = ref.current;
    if (!node || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const anim = node.animate(
      [
        { transform: "translateY(6px) scale(0.6)", opacity: 0 },
        { transform: "translateY(-6px) scale(1.15)", opacity: 1, offset: 0.2 },
        { transform: "translateY(-30px) scale(1)", opacity: 0 },
      ],
      { duration: 3200, easing: "ease-out", fill: "forwards" },
    );
    return () => anim.cancel();
  }, []);
  return (
    <span ref={ref} aria-hidden="true" className="pointer-events-none absolute right-28 top-2 text-2xl drop-shadow">
      {emoji}
    </span>
  );
}

function statusChips(row: PlayerRow): { tone: ChipTone; text: string }[] {
  const chips: { tone: ChipTone; text: string }[] = [];
  const { player } = row;
  if (player.eliminated) return [{ tone: "muted", text: "Ausgeschieden" }];
  if (row.isCurrent) chips.push({ tone: "accent", text: "Am Zug" });
  if (player.inJail) chips.push({ tone: "warn", text: "Gefängnis" });
  if (row.inDebt) chips.push({ tone: "danger", text: "In Schulden" });
  if (!row.connected && !row.isBot) chips.push({ tone: "danger", text: "Getrennt" });
  if (row.autoPlay) chips.push({ tone: "muted", text: "Auto-Modus" });
  if (player.jailCards.length > 0) chips.push({ tone: "muted", text: player.jailCards.length === 1 ? "Freikarte" : `${player.jailCards.length} Freikarten` });
  return chips;
}

function PlayerRowButton({ row, onOpen }: { row: PlayerRow; onOpen: () => void }) {
  const reactions = useStore((s) => s.reactions);
  const mine = reactions.filter((r) => r.playerId === row.player.id).slice(-1)[0];
  const { player } = row;
  const chips = statusChips(row);
  return (
    <li className="relative">
      <button
        type="button"
        onClick={onOpen}
        aria-label={`${player.name}${row.isSelf ? " (du)" : ""}, Bargeld ${formatMoney(player.cash)}, ${row.properties} Grundstücke. Besitz ansehen`}
        style={{ borderLeftColor: colorHex(player.color) }}
        className={`flex w-full items-center gap-3 rounded-xl border border-l-[6px] px-3 py-2.5 text-left transition-colors hover:bg-surface-2 ${
          row.isCurrent ? "border-y-accent border-r-accent bg-accent-soft" : "border-y-line border-r-line bg-surface"
        } ${player.eliminated ? "opacity-60" : ""}`}
      >
        <TokenIcon token={player.token} color={player.color} size={36} title={player.name} />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <span className={`truncate font-semibold ${player.eliminated ? "line-through" : ""}`}>{player.name}</span>
            {row.isSelf && <span className="text-xs text-ink-2">(du)</span>}
            {row.isBot && (
              <span className="shrink-0 rounded bg-surface-2 px-1.5 py-0.5 text-xs font-medium text-ink-2">{botLabel(row.member?.botLevel)}</span>
            )}
          </span>
          {chips.length > 0 && (
            <span className="mt-1 flex flex-wrap gap-1">
              {chips.map((c) => (
                <Chip key={c.text} tone={c.tone}>
                  {c.text}
                </Chip>
              ))}
            </span>
          )}
        </span>
        <span className="text-right">
          <span className="block text-base font-bold tabular-nums">{formatMoney(player.cash)}</span>
          <span className="block text-xs text-ink-2">
            {row.properties === 1 ? "1 Grundstück" : `${row.properties} Grundstücke`}
          </span>
        </span>
      </button>
      {mine && <FloatingEmoji key={mine.id} emoji={mine.emoji} />}
    </li>
  );
}

export function PlayerList({ room }: { room: RoomView }) {
  const rows = playerRows(room);
  const inspectPlayer = useUiStore((s) => s.inspectPlayer);
  const openTab = useUiStore((s) => s.openTab);
  const spectating = isSpectating(room);
  return (
    <div className="space-y-3">
      <ul className="space-y-2" aria-label="Spieler in Zugreihenfolge">
        {rows.map((row) => (
          <PlayerRowButton
            key={row.player.id}
            row={row}
            onOpen={() => (row.isSelf && !spectating ? openTab("assets") : inspectPlayer(row.player.id))}
          />
        ))}
      </ul>
      {room.spectators > 0 && (
        <p className="text-sm text-ink-2">{room.spectators === 1 ? "1 Zuschauer" : `${room.spectators} Zuschauer`}</p>
      )}
    </div>
  );
}
